/**
 * localStorage key holding the expiration of the temporary password most
 * recently issued in this browser — the timestamp only, never a user object.
 *
 * This used to ride along on the shared `user` key: the forgot-password /
 * reset-password / create-account flows stored the whole API envelope there
 * (`{ data: User }`) purely so the login page could reach
 * `data.tempPasswordExpiration`. That left one key holding two different shapes,
 * which every `getStoredUser()` reader then had to survive. The expiration now
 * has its own key with exactly one reader: the pre-submit temp-password check on
 * the login page.
 */
export const TEMP_PASSWORD_EXPIRATION_KEY = 'tempPasswordExpiration';

/**
 * Records the expiration of a newly issued temporary password.
 *
 * A missing, empty, or unusable value CLEARS the key instead of leaving the
 * previous one behind. The flows that call this always overwrote the stored
 * value wholesale, so "the latest response carries no expiration" has always
 * meant "there is nothing to enforce" — clearing preserves that. A usable string
 * is stored verbatim rather than re-serialized, so a value read straight off the
 * API envelope survives the round trip unchanged. Storage errors (e.g. privacy
 * mode) are swallowed; the worst case is that the expiry check simply doesn't fire.
 */
export function setTempPasswordExpiration(
  expiration: string | number | Date | null | undefined
): void {
  const stored = toStorableExpiration(expiration);
  try {
    if (stored === null) {
      localStorage.removeItem(TEMP_PASSWORD_EXPIRATION_KEY);
    } else {
      localStorage.setItem(TEMP_PASSWORD_EXPIRATION_KEY, stored);
    }
  } catch {
    // Storage unavailable — ignore.
  }
}

/**
 * Drops the recorded expiration.
 *
 * Called on a successful login so a temporary password that has since been
 * replaced can't leave an expiration behind to block a later sign-in. Logout is
 * already covered by the blanket `localStorage.clear()` in
 * `AuthenticationService.clearClientSession()` and the auth interceptor's
 * `clearClientState()`, so no extra teardown is wired there.
 */
export function clearTempPasswordExpiration(): void {
  try {
    localStorage.removeItem(TEMP_PASSWORD_EXPIRATION_KEY);
  } catch {
    // Storage unavailable — ignore.
  }
}

/**
 * Whether the recorded temporary-password expiration has already passed.
 *
 * Returns `false` when nothing is recorded or the recorded value isn't a
 * parseable date — an unknown expiration must never block a login, which is also
 * how the previous inline check behaved (it compared against `new Date('')`, so
 * a missing value produced `NaN` and fell through).
 */
export function isTempPasswordExpired(): boolean {
  let raw: string | null;
  try {
    raw = localStorage.getItem(TEMP_PASSWORD_EXPIRATION_KEY);
  } catch {
    return false;
  }
  if (!raw) {
    return false;
  }
  const expiresAt = new Date(raw).getTime();
  if (Number.isNaN(expiresAt)) {
    return false;
  }
  return expiresAt < Date.now();
}

/**
 * Normalizes an expiration to the string to store, or `null` to clear the key.
 *
 * The callers read this off an untyped API envelope, so the value is normalized
 * to a `Date` BEFORE parsing. In practice it arrives as an ISO string
 * (`"2026-07-14T01:44:52.280+00:00"`), but a numeric epoch has to be handed to
 * `new Date(number)` directly — `new Date(String(1751234567890))` is an Invalid
 * Date, which would silently switch the expiry gate off. A `Date` can't come off
 * JSON at all; it is accepted because a caller could construct one.
 *
 * A usable string is returned as received (trimmed only) so nothing is lost to a
 * re-serialization round trip; anything else usable is emitted as ISO. An
 * unparseable value clears the key, which is indistinguishable through this
 * module's public API from storing it: `isTempPasswordExpired()` returns `false`
 * either way (via `!raw` when cleared, via `Number.isNaN` when stored).
 */
function toStorableExpiration(
  expiration: string | number | Date | null | undefined
): string | null {
  if (!expiration) {
    return null;
  }
  const asString = typeof expiration === 'string' ? expiration.trim() : '';
  const parsed =
    expiration instanceof Date
      ? expiration
      : new Date(typeof expiration === 'number' ? expiration : asString);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }
  return asString !== '' ? asString : parsed.toISOString();
}
